import { withLoading } from "../shared/ui/load-modal";
import { reportDataCall, clientListDataCall } from "../core/api/report-api";
import { getFileUrl, getFirstWordFromFilename, fileNameFromUrl, hasV12InFilename } from "../shared/utils/helper-methods";
import { loadClientMapOnce, getClientMap, toClientLabel, getUniqueCarriers, filterClientNamesByQuery, filterClientNamesByQueryAndMapping } from "./client-data-manager";
import { createOption, clearOptions, debounce, safeText } from "../shared/ui/ui-helpers";

export type UiIds = { searchId: string; clientId: string; carrierId: string };
export type PopulationCallback = () => void;

export class ClientCarrierController {
  private search: HTMLInputElement | null;
  private clientSel: HTMLSelectElement | null;
  private carrierSel: HTMLSelectElement | null;
  private filteredNames: string[] = [];
  private hasMappingFilter: boolean | null;
  private populationCallback: PopulationCallback | null = null;
  private changeEventTimeout: number | null = null;

  constructor(private ids: UiIds, hasMappingFilter: boolean | null = null) {
    this.search = document.getElementById(ids.searchId) as HTMLInputElement | null;
    this.clientSel = document.getElementById(ids.clientId) as HTMLSelectElement | null;
    this.carrierSel = document.getElementById(ids.carrierId) as HTMLSelectElement | null;
    this.hasMappingFilter = hasMappingFilter;
  }

  public setPopulationCallback(callback: PopulationCallback) {
    this.populationCallback = callback;
  }

  public async init() {
    if (!this.clientSel || !this.carrierSel) return;

    await withLoading(loadClientMapOnce(clientListDataCall), "Loading clients...");

    const clientMap = getClientMap();
    if (this.hasMappingFilter !== null) {
      this.filteredNames = filterClientNamesByQueryAndMapping(clientMap, "", this.hasMappingFilter);
    } else {
      this.filteredNames = Object.keys(clientMap).sort((a, b) => a.localeCompare(b));
    }
    this.renderAll(true); // initial render
    this.wireEvents();

    // may be null if the file hasn't been saved yet
    let urlOrPath = await getFileUrl();

    if (urlOrPath) {
      const filenameOnly = fileNameFromUrl(urlOrPath);
      const firstWord = getFirstWordFromFilename(filenameOnly);
      const inputSearch = document.getElementById('clientSearchTab1') as HTMLInputElement | null;
      
      // Check for v12 in filename and display error if found
      if (hasV12InFilename(filenameOnly)) {
        const messageArea = document.getElementById("message-area") as HTMLElement;
        if (messageArea) {
          messageArea.textContent = "Error: v12 files are not supported for running reports";
          messageArea.style.color = "#a80000"; // Red for error
        }
      }
      
      if (inputSearch)
      {
        inputSearch.value = firstWord;
        
        inputSearch.dispatchEvent(new Event('input',  { bubbles: true }));
        // Use debounced change event instead of immediate
        this.dispatchChangeEventWithDelay();

      }
    }

    // Call population callback if set
    if (this.populationCallback) {
      this.populationCallback();
    }
  }

  private wireEvents() {
    const onSearch = debounce(() => {
      const clientMap = getClientMap();
      if (this.hasMappingFilter !== null) {
        this.filteredNames = filterClientNamesByQueryAndMapping(clientMap, this.search?.value ?? "", this.hasMappingFilter);
      } else {
        this.filteredNames = filterClientNamesByQuery(clientMap, this.search?.value ?? "");
      }
      this.renderAll(false);
    }, 200);
    this.search?.addEventListener("input", onSearch);

    this.clientSel!.addEventListener("change", () => {
      this.handleClientChange();
    });
  }

  private handleClientChange() {
    const selClient = this.clientSel!.value;
    this.carrierSel!.value = selClient === "0" ? "0" : this.pickCarrierForClient(selClient);
  }

  private renderAll(resetSelection: boolean) {
    const prevClient = this.clientSel!.value ?? "0";
    this.renderClients();
    this.renderCarriers();

    const keep = !resetSelection && prevClient !== "0" && this.filteredNames.includes(prevClient);
    if (keep) {
      this.clientSel!.value = prevClient;
      // Dispatch debounced change event to trigger carrier update, month lookup and button visibility update
      this.dispatchChangeEventWithDelay();
    } else {
      this.clientSel!.value = "0";
      // Dispatch debounced change event to trigger carrier update, button visibility update (hide button)
      this.dispatchChangeEventWithDelay();
      // New: after a fresh render (or when not keeping previous), attempt auto-select
      this.autoSelectSingleClientIfApplicable();
    }
  }

  private dispatchChangeEventWithDelay() {
    // Clear any existing timeout
    if (this.changeEventTimeout !== null) {
      clearTimeout(this.changeEventTimeout);
    }
    
    // Set new timeout for 2 seconds
    this.changeEventTimeout = window.setTimeout(() => {
      if (this.clientSel) {
        // First handle the internal carrier update
        this.handleClientChange();
        // Then dispatch the change event for external handlers (ReportUIManager)
        this.clientSel.dispatchEvent(new Event("change", { bubbles: true }));
      }
      this.changeEventTimeout = null;
    }, 1000);
  }

  private renderClients() {
    const sel = this.clientSel!;
    const clientMap = getClientMap();
    clearOptions(sel);
    sel.appendChild(createOption("0", "Select Client"));
    for (const name of this.filteredNames) {
      const rec = clientMap[name];
      if (rec) sel.appendChild(createOption(name, toClientLabel(name, rec)));
    }
  }

  private renderCarriers() {
    const sel = this.carrierSel!;
    const clientMap = getClientMap();
    clearOptions(sel);
    sel.appendChild(createOption("0", "Select Carrier"));
    for (const carrier of getUniqueCarriers(clientMap, this.filteredNames)) {
      sel.appendChild(createOption(carrier, carrier));
    }
  }

  private pickCarrierForClient(clientName: string) {
    const clientMap = getClientMap();
    const desired = safeText(clientMap[clientName]?.carrierName);
    if (!desired) return "0";
    return Array.from(this.carrierSel!.options).some((o) => o.value === desired) ? desired : "0";
  }

  /**
   * If the client select has exactly two options—"Select Client" and exactly one real client—
   * automatically select that client and set the carrier accordingly.
   */
  private autoSelectSingleClientIfApplicable() {
    const sel = this.clientSel!;
    // Expecting two options: [ "0" (Select Client), "<clientName>" ]
    if (sel.options.length === 2) {
      const onlyOption = sel.options[1];
      const onlyValue = onlyOption.value;
      if (onlyValue !== "0") {
        sel.value = onlyValue;
        // Use debounced change event to trigger carrier update and month lookup
        this.dispatchChangeEventWithDelay();
      }
    }
  }
}
